import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2, Plus, Send, ArrowLeft, CheckCircle2, ShieldCheck, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactSchema = z.object({
  name: z.string().min(2, 'Name muss mindestens 2 Zeichen haben'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
  phone: z.string().min(5, 'Telefonnummer ist erforderlich'),
  company: z.string().min(2, 'Firma ist erforderlich'),
  billing_address: z.string().min(10, 'Bitte vollständige Rechnungsadresse angeben'),
  delivery_address: z.string().optional(),
  same_address: z.boolean().optional(),
  desired_delivery_date: z.string().optional(),
  project_type: z.string().optional(),
  message: z.string().optional(),
  spam_answer: z.string().min(1, 'Bitte lösen Sie die Rechenaufgabe'),
});

const CartPage = () => {
  const navigate = useNavigate();
  const { items, removeItem, clearCart, itemCount } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [sameAddress, setSameAddress] = useState(true);
  const [spamQuestion, setSpamQuestion] = useState({ a: 0, b: 0, answer: 0 });

  // Generate spam protection question
  useEffect(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setSpamQuestion({ a, b, answer: a + b });
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      billing_address: '',
      delivery_address: '',
      same_address: true,
      desired_delivery_date: '',
      project_type: '',
      message: '',
      spam_answer: '',
    },
  });

  const handleDateSelect = (date) => {
    setDeliveryDate(date);
    if (date) {
      setValue('desired_delivery_date', format(date, 'dd.MM.yyyy', { locale: de }));
    }
  };

  const handleSameAddressChange = (checked) => {
    setSameAddress(checked);
    setValue('same_address', checked);
    if (checked) {
      setValue('delivery_address', '');
    }
  };

  const onSubmit = async (data) => {
    // Spam check
    if (parseInt(data.spam_answer) !== spamQuestion.answer) {
      toast.error('Falsche Antwort auf die Rechenaufgabe. Bitte versuchen Sie es erneut.');
      // Generate new question
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      setSpamQuestion({ a, b, answer: a + b });
      setValue('spam_answer', '');
      return;
    }

    if (items.length === 0) {
      toast.error('Bitte fügen Sie mindestens eine Kugel hinzu');
      return;
    }

    setIsSubmitting(true);
    try {
      const deliveryAddr = sameAddress ? data.billing_address : data.delivery_address;
      
      const payload = {
        contact: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company,
          delivery_address: `Rechnungsadresse:\n${data.billing_address}\n\nLieferadresse:\n${deliveryAddr}`,
          desired_delivery_date: data.desired_delivery_date || null,
          project_type: data.project_type || null,
        },
        spheres: items.map(item => ({
          size_mm: item.size_mm,
          color: item.color,
          coating: item.coating,
          options: item.options,
          quantity: item.quantity,
          notes: item.notes,
        })),
        message: data.message || null,
      };

      const response = await axios.post(`${API}/inquiries`, payload);
      
      if (response.data.status === 'success') {
        setIsSuccess(true);
        clearCart();
        toast.success('Anfrage erfolgreich versendet!');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getOptionLabel = (optionId) => {
    const labels = {
      natur: 'Natur',
      hanging: 'Abhängung',
      flat: 'Abgeflacht',
      ring: 'Unterlegring',
      a1: 'A1-Beschichtung',
    };
    return labels[optionId] || optionId;
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-4">
            Anfrage gesendet!
          </h1>
          <p className="text-zinc-400 mb-8">
            Vielen Dank für Ihre Anfrage. Sie erhalten in Kürze eine Bestätigung per E-Mail.
            Wir werden uns schnellstmöglich bei Ihnen melden.
          </p>
          <Link to="/">
            <Button 
              className="h-12 px-8 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90"
              data-testid="back-home-button"
            >
              Zurück zur Startseite
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* Header */}
        <div className="mb-8 md:mb-12">
          <p className="text-label text-primary mb-2">Schritt 2 von 2</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
            Anfrage senden
          </h1>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-zinc-400 mb-6">Ihr Warenkorb ist leer.</p>
            <Link to="/configurator">
              <Button 
                className="h-12 px-8 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90"
                data-testid="start-configuring-button"
              >
                Ei konfigurieren
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left: Cart Items */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold uppercase tracking-tight text-white">
                  Konfigurierte Eier ({itemCount})
                </h2>
                <Link to="/configurator">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-primary hover:text-primary/80"
                    data-testid="add-another-button"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Weitere hinzufügen
                  </Button>
                </Link>
              </div>

              <div className="space-y-4">
                {items.map((item, index) => (
                  <div
                    key={item.cartId}
                    className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-sm"
                    data-testid={`cart-item-${index}`}
                  >
                    <div className="flex gap-4">
                      {/* Egg Preview */}
                      <div 
                        className="w-12 h-16 flex-shrink-0 sphere-shadow"
                        style={{ 
                          backgroundColor: item.color,
                          borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%'
                        }}
                      />
                      
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-medium text-white">
                              Ei {item.size_mm}mm
                            </h3>
                            <p className="text-sm text-zinc-400">
                              Menge: {item.quantity}x
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.cartId)}
                            className="text-zinc-500 hover:text-destructive flex-shrink-0"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span 
                            className="inline-flex items-center gap-1 text-xs bg-zinc-800 px-2 py-1 rounded-sm"
                          >
                            <span 
                              className="w-3 h-3 rounded-sm"
                              style={{ backgroundColor: item.color }}
                            />
                            {item.color}
                          </span>
                          {item.coating && (
                            <span className="text-xs bg-zinc-800 px-2 py-1 rounded-sm text-zinc-300">
                              {item.coating}
                            </span>
                          )}
                          {item.options.map(opt => (
                            <span 
                              key={opt}
                              className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-sm"
                            >
                              {getOptionLabel(opt)}
                            </span>
                          ))}
                        </div>
                        
                        {item.notes && (
                          <p className="mt-2 text-xs text-zinc-500 truncate">
                            {item.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Contact Form */}
            <div>
              <h2 className="font-heading text-xl font-semibold uppercase tracking-tight text-white mb-6">
                Kontaktdaten
              </h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Name *</Label>
                    <Input
                      {...register('name')}
                      placeholder="Max Mustermann"
                      className="h-12 bg-zinc-900 border-zinc-800 focus:border-primary"
                      data-testid="input-name"
                    />
                    {errors.name && (
                      <p className="text-xs text-destructive mt-1">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Firma *</Label>
                    <Input
                      {...register('company')}
                      placeholder="Firma GmbH"
                      className="h-12 bg-zinc-900 border-zinc-800 focus:border-primary"
                      data-testid="input-company"
                    />
                    {errors.company && (
                      <p className="text-xs text-destructive mt-1">{errors.company.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">E-Mail *</Label>
                    <Input
                      {...register('email')}
                      type="email"
                      placeholder="max@beispiel.de"
                      className="h-12 bg-zinc-900 border-zinc-800 focus:border-primary"
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive mt-1">{errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Telefon *</Label>
                    <Input
                      {...register('phone')}
                      type="tel"
                      placeholder="+49 123 456789"
                      className="h-12 bg-zinc-900 border-zinc-800 focus:border-primary"
                      data-testid="input-phone"
                    />
                    {errors.phone && (
                      <p className="text-xs text-destructive mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-label text-zinc-400 block mb-2">Rechnungsadresse *</Label>
                  <Textarea
                    {...register('billing_address')}
                    placeholder="Straße, Hausnummer&#10;PLZ Ort&#10;Land"
                    className="min-h-[100px] bg-zinc-900 border-zinc-800 focus:border-primary resize-none"
                    data-testid="input-billing-address"
                  />
                  {errors.billing_address && (
                    <p className="text-xs text-destructive mt-1">{errors.billing_address.message}</p>
                  )}
                </div>

                <div className="flex items-center gap-3 py-2">
                  <Checkbox
                    id="same-address"
                    checked={sameAddress}
                    onCheckedChange={handleSameAddressChange}
                    data-testid="same-address-checkbox"
                  />
                  <Label htmlFor="same-address" className="text-sm text-zinc-400 cursor-pointer">
                    Lieferadresse entspricht der Rechnungsadresse
                  </Label>
                </div>

                {!sameAddress && (
                  <div>
                    <Label className="text-label text-zinc-400 block mb-2">Lieferadresse *</Label>
                    <Textarea
                      {...register('delivery_address')}
                      placeholder="Straße, Hausnummer&#10;PLZ Ort&#10;Land"
                      className="min-h-[100px] bg-zinc-900 border-zinc-800 focus:border-primary resize-none"
                      data-testid="input-delivery-address"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-label text-zinc-400 block mb-2">Gewünschter Liefertermin</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700"
                        data-testid="delivery-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                        {deliveryDate ? (
                          format(deliveryDate, 'dd. MMMM yyyy', { locale: de })
                        ) : (
                          <span className="text-zinc-500">Datum auswählen</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                      <Calendar
                        mode="single"
                        selected={deliveryDate}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        locale={de}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-label text-zinc-400 block mb-2">Projekt-Referenz</Label>
                  <Select onValueChange={(value) => setValue('project_type', value)}>
                    <SelectTrigger 
                      className="w-full h-12 bg-zinc-900 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700"
                      data-testid="project-type-select"
                    >
                      <div className="flex items-center">
                        <FolderOpen className="mr-2 h-4 w-4 text-zinc-500" />
                        <SelectValue placeholder="Wofür benötigen Sie die Kugeln?" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="messe" className="hover:bg-zinc-800">Messe / Ausstellung</SelectItem>
                      <SelectItem value="event" className="hover:bg-zinc-800">Event / Veranstaltung</SelectItem>
                      <SelectItem value="schaufenster" className="hover:bg-zinc-800">Schaufenster / Dekoration</SelectItem>
                      <SelectItem value="werbung" className="hover:bg-zinc-800">Werbung / Marketing</SelectItem>
                      <SelectItem value="kunst" className="hover:bg-zinc-800">Kunst / Installation</SelectItem>
                      <SelectItem value="film" className="hover:bg-zinc-800">Film / TV / Theater</SelectItem>
                      <SelectItem value="sonstiges" className="hover:bg-zinc-800">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-label text-zinc-400 block mb-2">Nachricht</Label>
                  <Textarea
                    {...register('message')}
                    placeholder="Weitere Anmerkungen oder Fragen..."
                    className="min-h-[100px] bg-zinc-900 border-zinc-800 focus:border-primary resize-none"
                    data-testid="input-message"
                  />
                </div>

                {/* Spam Protection */}
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                    <Label className="text-label text-zinc-400">Spam-Schutz *</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">
                      Was ist {spamQuestion.a} + {spamQuestion.b}?
                    </span>
                    <Input
                      {...register('spam_answer')}
                      type="number"
                      placeholder="?"
                      className="w-24 h-10 bg-zinc-900 border-zinc-800 focus:border-primary text-center"
                      data-testid="spam-answer"
                    />
                  </div>
                  {errors.spam_answer && (
                    <p className="text-xs text-destructive mt-2">{errors.spam_answer.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-primary text-black font-bold uppercase tracking-wider rounded-sm hover:bg-primary/90 btn-active text-lg mt-6"
                  data-testid="submit-inquiry-button"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Anfrage absenden
                    </>
                  )}
                </Button>

                <p className="text-xs text-zinc-500 text-center mt-4">
                  Mit dem Absenden akzeptieren Sie unsere{' '}
                  <Link to="/legal" className="text-primary hover:underline">AGB</Link>.
                </p>
              </form>
            </div>
          </div>
        )}

        {/* Back Link */}
        {items.length > 0 && (
          <div className="mt-8">
            <Link 
              to="/configurator"
              className="inline-flex items-center text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="back-to-configurator"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Konfigurator
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
